/**
 * AttachmentDropzone — drag-and-drop + "browse" picker + the file list.
 *
 * Validation (type / size / count) is client-side UX only; the server error
 * is authoritative and shows per file on the row. The component is
 * presentational: callers own the item list and decide whether a file is
 * queued locally (initial form) or uploaded (session exists).
 */
import { AttachFile as AttachIcon } from '@mui/icons-material';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useCallback, useRef, useState, type DragEvent, type ReactElement } from 'react';

import { AttachmentList } from '@/components/AttachmentList';
import type { AttachmentItem } from '@/hooks/useAttachmentUploads';
import {
  ALLOWED_ATTACHMENT_EXTENSIONS,
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS_PER_SESSION,
  MAX_ATTACHMENT_BYTES,
  formatLimitBytes,
  validateAttachmentFiles,
} from '@/lib/attachments';

export interface AttachmentDropzoneProps {
  items: AttachmentItem[];
  onAddFiles: (files: File[]) => void;
  onRemove: (key: string) => void;
  onRetry?: (key: string) => void;
  disabled?: boolean;
}

export function AttachmentDropzone({
  items,
  onAddFiles,
  onRemove,
  onRetry,
  disabled = false,
}: AttachmentDropzoneProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [rejections, setRejections] = useState<string[]>([]);

  const handleFiles = useCallback(
    (fileList: FileList | File[] | null) => {
      if (!fileList || disabled) return;
      const files = Array.from(fileList);
      if (files.length === 0) return;
      // Failed uploads are not stored server-side, so they don't count toward the cap.
      const existing = items.filter((it) => it.status !== 'error').length;
      const { accepted, errors } = validateAttachmentFiles(files, existing);
      setRejections(errors);
      if (accepted.length > 0) onAddFiles(accepted);
    },
    [items, disabled, onAddFiles],
  );

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <Stack spacing={1.5}>
      <Box
        data-testid="attachment-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        sx={(theme) => ({
          border: '1px dashed',
          borderColor: dragging ? 'primary.main' : 'divider',
          bgcolor: dragging ? 'action.hover' : 'transparent',
          borderRadius: 1,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          transition: theme.transitions.create(['border-color', 'background-color'], {
            duration: theme.transitions.duration.shorter,
          }),
        })}
      >
        <Button
          variant="outlined"
          startIcon={<AttachIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          sx={{ minHeight: 44 }}
        >
          Attach files
        </Button>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="body2">Drag files here, or browse.</Typography>
          <Typography variant="body2" color="text.secondary" id="attachment-help">
            Up to {MAX_ATTACHMENTS_PER_SESSION} files, {formatLimitBytes(MAX_ATTACHMENT_BYTES)} each (
            {ALLOWED_ATTACHMENT_EXTENSIONS.join(' ')}).
          </Typography>
        </Box>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={ATTACHMENT_ACCEPT}
          aria-label="Choose files to attach"
          data-testid="attachment-input"
          disabled={disabled}
          onChange={(e) => {
            handleFiles(e.target.files);
            // Allow re-picking the same file after a rejection.
            e.target.value = '';
          }}
        />
      </Box>

      {rejections.length > 0 && (
        <Alert severity="warning" role="alert" onClose={() => setRejections([])}>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {rejections.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </Box>
        </Alert>
      )}

      <AttachmentList items={items} onRemove={onRemove} onRetry={onRetry} disabled={disabled} />
    </Stack>
  );
}
